import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LeftChevron, RightChevron } from "../../components/chevron";
import Transaction from "../../components/transaction/Transaction";
import { DateFormat, format, isSameDay } from "../../dates";
import { useLocale } from "../../i18n";
import { useMetrics } from "../../metrics.ts";
import {
  CountryIsoCode,
  useCountryISO2Code,
} from "../add-address/useCountryISO2Code";
import { PayrollCycle, usePayrollCycles } from "./usePayrollCycle";
import { useTransactionAmount } from "./useTransactionAmount";
import { useTransactionName } from "./useTransactionName";
import { useTransactionTag } from "./useTransactionTag";
import { useTransactionTaxExemption } from "./useTransactionTaxExemption";
import {
  Transaction as TransactionType,
  useTransactions,
} from "./useTransactions";

type TransactionProps = TransactionType & {
  isCountryLoading: boolean;
  countryISO2Code: CountryIsoCode;
};

const FIRST_PAYROLL_INDEX = -1;

const TransactionComponent = ({
  type,
  merchant,
  state,
  rejectedReason,
  fundOrigins,
  isCountryLoading,
  countryISO2Code,
  amount,
  taxExemption,
  benefit,
}: TransactionProps) => {
  const name = useTransactionName({
    type,
    merchantName: merchant?.name,
  });
  const tag = useTransactionTag({
    state,
    rejectedReason,
    fundOrigins,
    isCountryLoading,
    countryISO2Code,
  });

  const amountCopy = useTransactionAmount({ amount, type });
  const taxExemptionCopy = useTransactionTaxExemption({ taxExemption, state });
  return (
    <Transaction
      name={name}
      amount={amountCopy}
      tag={tag}
      taxExemption={taxExemptionCopy}
      fundOrigins={fundOrigins}
      state={state}
      type={type}
      merchant={merchant}
      benefit={benefit}
    />
  );
};

const Transactions = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { trackAction, pageView } = useMetrics();
  const [currentPayroll, setCurrentPayroll] = useState<PayrollCycle | null>(
    null
  );
  const [currentPayrollIndex, setCurrentPayrollIndex] =
    useState<number>(FIRST_PAYROLL_INDEX);

  const { isLoading: isLoadingPayrollCycle, payrollCycles } =
    usePayrollCycles();
  const { isLoading: isLoadingTransactions, transactions } =
    useTransactions(currentPayroll);
  const { isLoading: isCountryLoading, countryISO2Code } = useCountryISO2Code();

  const getCurrentPayrollText = () => {
    if (!currentPayroll) return;

    const dateOfCurrentPayRoll = new Date(
      `${currentPayroll.fiscalYear}-${currentPayroll.payrollMonth}-01`
    );

    return format(dateOfCurrentPayRoll, DateFormat.SHORT_MONTH_AND_YEAR);
  };

  const onPreviousPayrollCycle = () => {
    if (currentPayrollIndex > 0) {
      setCurrentPayrollIndex(currentPayrollIndex - 1);
    }
  };

  const onNextPayrollCycle = () => {
    if (currentPayrollIndex < payrollCycles.length - 1) {
      setCurrentPayrollIndex(currentPayrollIndex + 1);
    }
  };
  const previousMonthDisabled = currentPayrollIndex === 0;
  const nextMonthDisabled = currentPayrollIndex === payrollCycles.length - 1;
  const hasTransactions = transactions.length > 0;

  useEffect(() => {
    pageView("Transactions");
  }, []);

  useEffect(() => {
    if (isLoadingTransactions) return;
    if (!hasTransactions) {
      trackAction.showed({
        screenName: "Transactions",
        componentName: "Empty Transactions list",
        additionalProperties: {
          payrollMonth: currentPayroll?.payrollMonth,
        },
      });
    } else {
      trackAction.showed({
        screenName: "Transactions",
        componentName: "Transactions list",
        additionalProperties: {
          payrollMonth: currentPayroll?.payrollMonth,
        },
      });
    }
  }, [isLoadingTransactions, hasTransactions, currentPayroll]);

  useEffect(() => {
    if (isLoadingPayrollCycle) return;

    if (payrollCycles.length === 0) return;

    setCurrentPayrollIndex(payrollCycles.length - 1);
  }, [isLoadingPayrollCycle, payrollCycles, locale]);

  useEffect(() => {
    if (currentPayrollIndex < 0) return;

    setCurrentPayroll(payrollCycles[currentPayrollIndex]);
  }, [setCurrentPayroll, payrollCycles, currentPayrollIndex]);

  return (
    <div className="bg-gray-50">
      <div className="flex items-center justify-between self-stretch gap-6 w-[600px] mb-6">
        <h1 className="font-semibold text-[28px] leading-[48px] text-gray-900 items-center">
          {t("transactions.movements")}
        </h1>
        {!isLoadingPayrollCycle && currentPayroll && (
          <div className="flex justify-center items-center">
            <button
              onClick={onPreviousPayrollCycle}
              className="mr-3.5"
              aria-label="Anterior mes"
              aria-disabled={previousMonthDisabled}
            >
              <LeftChevron disabled={previousMonthDisabled} />
            </button>
            <p className="text-sm font-normal text-gray-900 min-w-[120px] text-center mr-3.5">
              {getCurrentPayrollText()}
            </p>
            <button
              onClick={onNextPayrollCycle}
              aria-label="Siguiente mes"
              aria-disabled={nextMonthDisabled}
            >
              <RightChevron disabled={nextMonthDisabled} />
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-center p-4 rounded-3xl bg-white max-w-[600px]">
        {!isLoadingTransactions ? (
          hasTransactions ? (
            transactions.map((transaction, i) => (
              <div key={transaction.id}>
                {!isSameDay(
                  transaction.userTransactionDate,
                  transactions[i - 1]?.userTransactionDate
                ) ? (
                  <div className="flex mb-2 mt-4 py-0 px-4 items-center gap-2 self-stretch">
                    <p className="text-gray-700 text-xs font-normal tracking-wider leading-4">
                      {format(
                        transaction.userTransactionDate,
                        DateFormat.DAY_MONTH_AND_YEAR
                      )}
                    </p>
                    <div className="h-[1px] bg-gray-100 flex-1" />
                  </div>
                ) : (
                  <div className="h-[1px] bg-gray-100 flex-1 mx-4 my-2" />
                )}
                <TransactionComponent
                  {...transaction}
                  isCountryLoading={isCountryLoading}
                  countryISO2Code={countryISO2Code}
                />
              </div>
            ))
          ) : (
            <div>
              <h1 className="text-gray-900 font-medium text-lg">
                {t("transactions.empty-state.title")}
              </h1>
              <span className="text-gray-700 text-sm font-normal leading-5">
                {t("transactions.empty-state.description")}
              </span>
            </div>
          )
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export { Transactions };
